import { chunk, compact, get, has, isEmpty, uniq, first, reject } from 'lodash';
import { scriptTypes, applicationTypes } from 'constants/scripts';

// resources
import { resourceType, SCRIPT_BATCH_FETCH_SIZE } from 'constants/loading';
import { findResource } from 'loading/finders';

import { findProductById } from 'products/finders';
import {
  fetchDuplicateManualScriptTemplate,
  fetchScriptByIdTankMix,
  fetchManyScriptsTankMix,
} from 'scripts/loaders';
import {
  sendFetchManualScriptTemplate,
  sendFetchScriptList
} from 'scripts/senders';
import {
  processScript,
  processScriptListForFieldId,
  processManyScripts,
  processScriptsSummariesForFieldId,
  processScriptsForActiveOperation
} from 'scripts/processors';

// selectors
import { getActiveFieldUuid, getActiveUserId } from 'current/selectors';
import { getAllFieldUuids, getFieldsUuidsForActiveOperation } from 'fields/selectors';
import {
  isNewAdvancedScript,
  getApplicationTypeForScriptType,
  getPrimaryScriptsForFieldId,
  getScriptById,
  getScriptsForFieldId,
  getScriptTypeForScript,
  getScriptsSortedForManageScriptList,
  areRawScriptsForFieldUnLoaded,
  getScriptsForActiveFieldFromRawList
} from 'scripts/selectors';
import { getProductApplicationProductsInZones } from 'scripts/zones/productApplication-selectors';
import { areScriptsLoadImprovementsEnabled } from 'user/selectors';
import { getRawScriptList } from 'export-selector/selectors';
import { delayPromise } from 'util/promise';
import generateUuid from 'uuid/v4';
import { formatDateToISO8601 } from 'util/formatters/dateFormatter';
import { getGeneratedZonesfeatures } from 'manual/util/zone-generation';

const getTankMixProducts = (geometry) => {
  const features = get(geometry, 'features', []);
  const chemicalMix = compact(features.map((application) => application.properties['chemical-mix']));
  if(isEmpty(chemicalMix)) {
    return [];
  }
  return first(chemicalMix);
};

const getScriptProductsFromSummary = (script) => {
  const { summary, zones, geometry } = script;
  const scriptType = getScriptTypeForScript(script);

  const tankMixProducts = getTankMixProducts(geometry);
  if(!isEmpty(tankMixProducts)) {
    return tankMixProducts;
  }

  const scriptSummary = get(summary, scriptType, []);
  const products = reject(scriptSummary, ['product', null]);
  if(!isEmpty(products)) {
    return compact(scriptSummary.map(({ product }) => ({
      ...product,
      id: product.uuid || product.id
    })));
  }

  // last resort, take from feature properties
  if (zones) {
    return getProductApplicationProductsInZones({ scriptType, zones });
  }
  return [];
};

export const findScriptProducts = ({
  dispatch,
  getState,
  script,
  templateParams={}
}) => {
  const scriptType = getScriptTypeForScript(script);
  const applicationType = getApplicationTypeForScriptType(scriptType);
  let scriptProducts = [];
  const isApplication = applicationType === applicationTypes.APPLICATION;

  // take it from the template params because a new script won't have the products in it
  if (!isEmpty(templateParams)) {
    const { advancedProducts, plantingPlanParams } = templateParams;
    const { products: manualProducts } = plantingPlanParams;

    // fertility scripts built off advanced planting scripts will look like a new advanced script unless you
    // also select for the script type.
    const plantingPlanParamsProducts = scriptType === scriptTypes.PLANTING && isNewAdvancedScript(script)
      ? advancedProducts
      : manualProducts;

    const applicationProducts = isApplication
      ? get(plantingPlanParamsProducts, applicationTypes.APPLICATIONV2, []) 
      : get(plantingPlanParamsProducts, `${applicationType}`, {});

    if(isApplication) {
      scriptProducts = applicationProducts.map(product => ({ id: product }));
    } else{
      scriptProducts = Object.values(applicationProducts).map((product) => {
        return has(product, 'id') ? product : { id: product };
      });  
    }
  } else {
    scriptProducts = getScriptProductsFromSummary(script);
  }

  scriptProducts = uniq(compact(scriptProducts)).filter((ele) => ele.id !== null);

  return Promise.all(
    scriptProducts.map(({ id }) => findProductById({ applicationType, dispatch, getState, id }))
  );
};

export const findScriptById = ({ dispatch, getState, fieldId, scriptId, forceFetch=false }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const script = getScriptById(state, scriptId);

    if( script && script.zones && !forceFetch ) {
      return script;
    }
    const userId = getActiveUserId(state);

    return fetchScriptByIdTankMix({ userId, fieldId, scriptId })
    .then((rawScript) => {
      return processScript({ dispatch, getState, fieldId, rawScript});
    });
  });
};

export const findScriptListForFieldId = ({ dispatch, getState, fieldId, forceFetch = false }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const scripts = getScriptsForFieldId(state, fieldId);
    if( !isEmpty(scripts) && !forceFetch) {
      return scripts;
    }
    const userId = getActiveUserId(state);
    const fieldUuids = getAllFieldUuids(state);
    return sendFetchScriptList({ userId, fieldUuids })
    .then( (rawList) => processScriptListForFieldId({ dispatch, getState, fieldId, rawList }) );
  });
};

export const findScriptsSummariesForFieldId = ({ dispatch, getState, fieldId, forceReload = false }) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const fieldUuid = getActiveFieldUuid(state);

  const resourceSpec = {
    type: resourceType.SCRIPT_LIST,
    selector: () => getScriptsForActiveFieldFromRawList(state),
    fetcher: () => sendFetchScriptList({ userId, fieldUuids: [fieldUuid] }),
    processor: ({ data: rawList }) => processScriptsSummariesForFieldId({ dispatch, fieldId, fieldUuid, rawList }),
    forceReload: areRawScriptsForFieldUnLoaded(state, fieldUuid)
  };
  return findResource({ dispatch, getState, forceReload, forceWait: true, ...resourceSpec });
};

export const findScriptsForActiveOperation = ({ dispatch, getState }) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const fieldUuidsForActiveOperation = getFieldsUuidsForActiveOperation(state);
  const fieldUuids = fieldUuidsForActiveOperation.filter((uuid) => areRawScriptsForFieldUnLoaded(state, uuid));

  const resourceSpec = {
    type: resourceType.RAW_SCRIPTS_LIST,
    selector: () => getRawScriptList(state),
    fetcher: () => sendFetchScriptList({ userId, fieldUuids }),
    processor: ({ data: rawList }) => processScriptsForActiveOperation({ dispatch, rawList, fieldUuids }),
    forceReload: fieldUuids.length,
    forceWait: true
  };
  return findResource({ dispatch, getState, ...resourceSpec });
};

export const findPrimaryScriptListForFieldId = ({
  dispatch,
  getState,
  fieldId,
  forceReload=false,
  forceWait=false,
  onError
}) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const fieldUuids = [getActiveFieldUuid(state)];
  const resourceSpec = {
    type: resourceType.PRIMARY_SCRIPT_LIST,
    selector: () => getPrimaryScriptsForFieldId(state, fieldId),
    fetcher: () => sendFetchScriptList({ userId, fieldUuids, primaryScriptsOnly: true }),
    processor: ( rawList ) => {
      return processScriptListForFieldId({
        dispatch, getState, fieldId, rawList
      });
    }
  };

  return findResource({ dispatch, getState, forceReload, forceWait, onError, ...resourceSpec });
};

const fetchBulkScripts = (state, fieldId, batch)=>{
  const userId = getActiveUserId(state);

  return fetchManyScriptsTankMix({ userId, fieldId, scriptIds: batch });
};

export const batchFindScriptsForFieldId = ({ dispatch, getState, fieldId, scripts = [] }) => {
  const scriptIdsOfIncomplete = [];
  const state = getState();
  const areScriptsPerformanceEnabled = areScriptsLoadImprovementsEnabled(state);
  const sorted = getScriptsSortedForManageScriptList(state, scripts);
  sorted.forEach( (script) => {
    const isScriptIncomplete = areScriptsPerformanceEnabled ? !getScriptById(state, script.id)?.zones : !script.zones;
    if(isScriptIncomplete) {
      scriptIdsOfIncomplete.push(script.id);
    }
  });
  if(!scriptIdsOfIncomplete.length) {
    return scripts;
  }
  const batches = chunk(scriptIdsOfIncomplete, SCRIPT_BATCH_FETCH_SIZE);
  return Promise.all( batches.map( (batch) => {
    return delayPromise(fetchBulkScripts(state, fieldId, batch))
    .then( (rawScripts) => processManyScripts({ dispatch, getState, fieldId, rawScripts }) );
  }));
};

export const findAllScriptsForFieldId = ({ dispatch, getState, fieldId, ignoreRoute = false }) => {
  return areScriptsLoadImprovementsEnabled(getState())
    ? findScriptsSummariesForFieldId({ dispatch, getState, fieldId })
    .then((allScripts) => batchFindScriptsForFieldId({ dispatch, getState, fieldId, scripts: allScripts }))
    .then(() => findScriptsSummariesForFieldId({ dispatch, getState, fieldId }))
    : findScriptListForFieldId({ dispatch, getState, fieldId, ignoreRoute })
    .then((allScripts) => batchFindScriptsForFieldId({ dispatch, getState, fieldId, scripts: allScripts }))
    .then(() => findScriptListForFieldId({ dispatch, getState, fieldId, ignoreRoute })); // return all, not just newly added
};

export const findNewManualScript = ({ fieldId, templateParams, userId }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const { activityUuid, cropId, scriptIdForManagementZones } = templateParams;
    if (scriptIdForManagementZones) {
      return fetchDuplicateManualScriptTemplate({
        activityUuid,
        fieldId,
        scriptId: scriptIdForManagementZones,
        userId
      });
    }
    return sendFetchManualScriptTemplate({ activityUuid, cropId, fieldId, templateParams, userId });
  });
};

export const findManualZoneGeneratorScript = ({fieldId, templateParams, getState}) => {
  return Promise.resolve().then(() => {
    const state = getState();
    const { activityUuid, plantingPlanParams } = templateParams;
    const { features, summaryZones } = getGeneratedZonesfeatures(state);
    return {
      'activity-type': get(plantingPlanParams, `activityType`),
      'activity-uuid': activityUuid,
      'cropping-year': get(state, 'current.productYear'),
      'field-id': fieldId,
      'field-uuid': getActiveFieldUuid(state),
      'fmz-id': generateUuid(),
      'fmz-name': 'generated-fmz',
      'fmz-version': 0,
      'update-time': formatDateToISO8601(new Date()),
      primary: false,
      tag: 'manual',
      id: generateUuid(),
      meta: {},
      summary: summaryZones,
      geometry: {
        crs: {},
        type: 'FeatureCollection',
        features: features
      }
    };
  });
};
